/**
 * «منبعِ دیگری چیزِ دیگری می‌گوید.»
 *
 * ═══ چرا اصلاً وجود دارد ═══
 *
 * تا ۲۰۲۶-۰۸-۲۲ وقتی دو منبع سرِ ساعت یا مکانِ یک رویداد اختلاف داشتند،
 * کرالر رویداد را **اصلاً منتشر نمی‌کرد** و می‌فرستادش به صفِ بازبینی.
 * اندازه‌گیری نشان داد آن قاعده چه چیزی را نگه داشته بود: ۷۶۲۴ رویداد در صف
 * در برابر ۳۹۰۸ منتشرشده، که ۷۳۵۷ تایش دلیلش `field_conflict` بود. دو تا از
 * هر سه رویداد هرگز به اپ نمی‌رسیدند و صف هم هرگز خالی نمی‌شد.
 *
 * تصمیمِ تازه: وقتی نمی‌دانیم کدام درست است، انتخابِ دستی جوابش نیست —
 * نشان دادنِ هر دو است. رویداد منتشر می‌شود و کاربر خودش قضاوت می‌کند.
 *
 * ═══ از کجا می‌آید ═══
 *
 * از `public.events.metadata.field_alternatives`، که کرالر می‌نویسد. عمداً
 * ستونِ تازه‌ای برایش ساخته نشد: اسکیمای `public` مالِ goout است و کرالر
 * نباید هر بار برای یک فیلدِ تازه یک مایگریشن این‌طرف لازم داشته باشد.
 * یعنی **این فیچر هیچ مایگریشنی ندارد؛ فقط رندر است.**
 *
 * ⚠ همه‌چیز این‌جا محافظه‌کارانه parse می‌شود. `metadata` قراردادی است که
 * مخزنِ دیگری می‌نویسد؛ یک شکلِ غیرمنتظره باید یعنی «چیزی نشان نده»، نه
 * یک صفحه‌ی سفید.
 */
import type {
  AltFieldName, EventItem, FieldAlternative, FieldAlternatives,
} from '../domain/types.ts';
import type { ItemFieldName } from './itemFields.ts';
import { formatTime, formatWeekdayDateShort, weekdayLabel } from './dates.ts';
import { t } from './i18n.ts';

/**
 * فیلدهایی که می‌پذیریم. هر چیزِ دیگری در `field_alternatives` نادیده گرفته
 * می‌شود — یک کلیدِ ناشناخته یعنی کرالر جلوتر از این کد است، نه اینکه اپ
 * باید چیزی را حدس بزند و نشان بدهد.
 */
const KNOWN: AltFieldName[] = [
  'starts_at', 'ends_at', 'venue_name', 'venue_address',
  'price_note', 'is_free', 'lat', 'lng', 'weekdays',
];

/** کدام ردیفِ صفحه‌ی جزئیات صاحبِ کدام فیلد است (نگاه کن به itemFields.ts). */
const ROW_OF: Record<AltFieldName, ItemFieldName> = {
  starts_at: 'when', ends_at: 'when', weekdays: 'when',
  venue_name: 'where', venue_address: 'where', lat: 'where', lng: 'where',
  price_note: 'price', is_free: 'price',
};

const isObject = (x: unknown): x is Record<string, unknown> =>
  typeof x === 'object' && x !== null && !Array.isArray(x);

/**
 * یک ورودیِ خام → `FieldAlternative`، یا `null` اگر شکلش نخورد.
 *
 * `value` عمداً `unknown` می‌ماند و همین‌جا رشته نمی‌شود: قالب‌بندیِ زمان به
 * زبان و منطقه وابسته است و باید موقعِ رندر انجام شود، نه موقعِ parse.
 */
const one = (raw: unknown): FieldAlternative | null => {
  if (!isObject(raw)) return null;
  const source = typeof raw.source === 'string' ? raw.source.trim() : '';
  if (!source) return null;
  return {
    value: raw.value,
    source,
    url: typeof raw.url === 'string' && raw.url.trim() ? raw.url.trim() : null,
    selected: raw.selected === true,
  };
};

/**
 * `metadata` خام → جایگزین‌های قابلِ نمایش.
 *
 * فیلدی که فقط **یک** ادعا دارد حذف می‌شود: «یک منبع می‌گوید ۱۹:۰۰» اختلاف
 * نیست و نشان دادنش فقط سر و صداست.
 */
export const parseAlternatives = (metadata: unknown): FieldAlternatives | null => {
  if (!isObject(metadata)) return null;
  const raw = metadata.field_alternatives;
  if (!isObject(raw)) return null;

  const out: FieldAlternatives = {};
  for (const field of KNOWN) {
    const list = raw[field];
    if (!Array.isArray(list)) continue;
    const parsed = list.map(one).filter((x): x is FieldAlternative => !!x);
    if (parsed.length > 1) out[field] = parsed;
  }
  return Object.keys(out).length ? out : null;
};

/** یک ادعا، آماده‌ی نشان دادن: «۲۰:۰۰ — todocanada ↗». */
export interface AltLine {
  field: AltFieldName;
  /** متنِ خودِ مقدار، به همان زبان و قالبی که مقدارِ اصلی رندر می‌شود. */
  value: string;
  source: string;
  url?: string | null;
}

/** عدد یا رشته‌ی عددی → عدد؛ هر چیزِ دیگری `null`. */
const num = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

/**
 * مقدارِ خام → متن، بسته به فیلدش.
 *
 * ⚠ زمان با همان `formatTime`/`formatWeekdayDateShort` رندر می‌شود که مقدارِ
 * اصلیِ بالای همان ردیف را رندر کرده. اگر جایگزین با یک قالبِ دیگر نوشته
 * شود، دو عددِ کنارِ هم قابلِ مقایسه نیستند و کلِ کار بی‌فایده است.
 *
 * ⚠ روزِ جایگزین هم نوشته می‌شود، نه فقط ساعتش: دو منبع که «۱۹:۰۰» و
 * «۲۰:۰۰» می‌گویند ممکن است اصلاً دو **روزِ** متفاوت را بگویند، و نشان دادنِ
 * فقط ساعت آن را پنهان می‌کند.
 */
const valueText = (field: AltFieldName, value: unknown): string => {
  switch (field) {
    case 'starts_at':
    case 'ends_at': {
      if (typeof value !== 'string' || !value.trim()) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return [formatWeekdayDateShort(value), formatTime(value)].filter(Boolean).join(' ');
    }
    case 'weekdays': {
      if (!Array.isArray(value)) return '';
      return weekdayLabel(value.map(num).filter((n): n is number => n !== null));
    }
    case 'is_free':
      if (typeof value !== 'boolean') return '';
      return value ? t('free') : t('notFree');
    case 'lat':
    case 'lng': {
      const n = num(value);
      // چهار رقم اعشار ≈ ۱۱ متر. بیشترش فقط عددِ طولانی‌تری است که هیچ‌کس
      // با چشم مقایسه‌اش نمی‌کند.
      return n === null ? '' : n.toFixed(4);
    }
    default:
      return typeof value === 'string' ? value.trim() : '';
  }
};

/**
 * جایگزین‌هایی که باید کنارِ یک ردیفِ مشخصِ صفحه‌ی جزئیات بیایند.
 *
 * فقط ادعاهای **انتخاب‌نشده** برمی‌گردند: آنکه `selected` است همان چیزی است
 * که چند پیکسل بالاتر نوشته شده، و تکرارش یعنی کاربر دو بار یک حرف را
 * بخواند و دنبالِ فرقشان بگردد.
 *
 * ادعاهای تکراری (همان مقدار از دو منبع) یکی می‌شوند، با نامِ منبعِ اول:
 * «۲۰:۰۰ از todocanada» و «۲۰:۰۰ از eventbrite» یک خبرند.
 */
export const altLinesFor = (
  alts: FieldAlternatives | null | undefined, row: ItemFieldName,
): AltLine[] => {
  if (!alts) return [];
  const out: AltLine[] = [];
  const seen = new Set<string>();
  for (const field of KNOWN) {
    if (ROW_OF[field] !== row) continue;
    for (const a of alts[field] ?? []) {
      if (a.selected) continue;
      const value = valueText(field, a.value);
      if (!value) continue;
      const key = `${field}|${value}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ field, value, source: a.source, url: a.url ?? null });
    }
  }
  return out;
};

/** آیا این آیتم اصلاً جایگزینی دارد؟ برای شرطِ ارزانِ رندر. */
export const hasAlternatives = (e: Pick<EventItem, 'alternatives'>): boolean =>
  !!e.alternatives && Object.keys(e.alternatives).length > 0;
